<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6 text-center">
    <!-- 403 Gate -->
    <div
      v-if="permissionsReady && !can.view"
      class="mb-6 bg-white border border-gray-200 rounded-2xl shadow-sm p-10 text-center"
      role="alert"
    >
      <div
        class="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 text-red-600 grid place-items-center"
      >
        <svg viewBox="0 0 24 24" class="w-6 h-6">
          <path fill="currentColor" d="M11 7h2v6h-2zm0 8h2v2h-2z" />
          <path fill="currentColor" d="M1 21h22L12 2 1 21z" />
        </svg>
      </div>
      <h2 class="font-bold text-gray-900 mb-1">403 — You don't have permission</h2>
      <p class="text-gray-600">Please contact the administrator to enable access.</p>
    </div>

    <template v-else-if="permissionsReady && can.view">
      <h1 class="text-start text-[22px] sm:text-[24px] leading-7 font-bold text-gray-900 mb-12">
        Edit Permissions
      </h1>

      <div
        class="text-lg text-gray-800 font-semibold mb-12 flex flex-wrap justify-center gap-x-24 gap-y-2"
      >
        <div>
          Name : <span class="font-normal text-base">{{ header.name }}</span>
        </div>
        <div>
          Email : <span class="font-normal text-base">{{ header.email }}</span>
        </div>
      </div>

      <!-- Loading/Error -->
      <div class="max-w-2xl mx-auto mb-6">
        <div v-if="loading" class="text-sm text-gray-600 text-left">Loading...</div>
        <div v-else-if="errorMsg" class="text-sm text-red-600 text-left">{{ errorMsg }}</div>
      </div>

      <!-- Dropdown -->
      <div class="max-w-2xl mx-auto mb-12">
        <SearchableSelect
          v-model="selectedKey"
          :options="optionsForSelect"
          placeholder="Select Permission Group"
        />
      </div>

      <!-- Toggles Card -->
      <div class="max-w-2xl mx-auto mb-12">
        <div
          class="bg-gradient-to-b from-gray-50 to-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-6"
        >
          <div class="flex items-center justify-between mb-4 text-sm font-semibold text-gray-700">
            <button type="button" class="underline" @click="checkAll(true)">Select all</button>
            <button type="button" class="underline" @click="checkAll(false)">Unselect all</button>
          </div>
          <div class="space-y-3 text-left">
            <ToggleRow
              v-for="perm in filteredPermissions"
              :key="perm.value"
              :label="perm.label"
              :modelValue="isChecked(perm.value)"
              @update:modelValue="(v) => togglePermission(perm.value, v)"
            />
          </div>
        </div>

        <div class="mt-6">
          <button
            type="button"
            :disabled="saving || !can.edit"
            @click="handleSave"
            class="w-full h-10 rounded-full text-white font-semibold shadow-sm hover:opacity-95 disabled:opacity-60"
            :style="{ backgroundColor: '#5B9AC5' }"
          >
            {{ saving ? 'Saving...' : `Save Changes (${selectedCount})` }}
          </button>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="text-sm text-gray-600">Loading...</div>
    </template>
  </div>
</template>

<script>
import api from '@/api'
import Swal from 'sweetalert2'
import { h, ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { initPermissions, has, loaded as permLoaded } from '@/utils/permissions'

/* ---- ToggleRow ---- */
const ToggleRow = {
  name: 'ToggleRow',
  props: {
    modelValue: { type: Boolean, default: false },
    label: { type: String, required: true },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const onChange = (e) => emit('update:modelValue', e.target.checked)
    return () =>
      h('label', { class: 'flex items-center gap-3 select-none cursor-pointer justify-start' }, [
        h('input', { type: 'checkbox', checked: props.modelValue, onChange, class: 'sr-only' }),
        h(
          'div',
          {
            class:
              'w-11 h-6 rounded-full relative transition ' +
              (props.modelValue ? 'bg-green-500' : 'bg-gray-200'),
          },
          [
            h('span', {
              class:
                'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ' +
                (props.modelValue ? 'translate-x-5' : 'translate-x-0'),
            }),
          ]
        ),
        h('span', { class: 'text-sm font-semibold text-gray-800' }, props.label),
      ])
  },
}

/* ---- SearchableSelect ---- */
const SearchableSelect = {
  name: 'SearchableSelect',
  props: {
    modelValue: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Select...' },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const open = ref(false)
    const search = ref('')
    const hoverIndex = ref(-1)
    const rootEl = ref(null)
    const inputEl = ref(null)

    const filtered = computed(() => {
      const q = search.value.trim().toLowerCase()
      const arr = props.options || []
      if (!q) return arr
      return arr.filter((o) => String(o.label).toLowerCase().includes(q))
    })

    const label = computed(() => {
      const curr = (props.options || []).find((o) => o.key === props.modelValue)
      return curr ? curr.label : props.placeholder
    })

    function openList() {
      if (open.value) return
      open.value = true
      nextTick(() => {
        const idx = filtered.value.findIndex((o) => o.key === props.modelValue)
        hoverIndex.value = idx >= 0 ? idx : filtered.value.length ? 0 : -1
        inputEl.value?.focus()
        inputEl.value?.select?.()
      })
    }
    function closeList() {
      open.value = false
      search.value = ''
      hoverIndex.value = -1
    }
    function selectAt(index) {
      if (index < 0 || index >= filtered.value.length) return
      emit('update:modelValue', filtered.value[index].key)
      closeList()
    }
    function onKeydown(e) {
      if (!open.value) {
        if (['ArrowDown', 'Enter', ' '].includes(e.key)) {
          e.preventDefault()
          openList()
        }
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (!filtered.value.length) return
        hoverIndex.value = hoverIndex.value < filtered.value.length - 1 ? hoverIndex.value + 1 : 0
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (!filtered.value.length) return
        hoverIndex.value = hoverIndex.value > 0 ? hoverIndex.value - 1 : filtered.value.length - 1
      } else if (e.key === 'Enter') {
        e.preventDefault()
        selectAt(hoverIndex.value)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        closeList()
      }
    }
    function onDocClick(e) {
      if (!rootEl.value) return
      if (!rootEl.value.contains(e.target)) closeList()
    }
    onMounted(() => document.addEventListener('click', onDocClick))
    onBeforeUnmount(() => document.removeEventListener('click', onDocClick))

    return {
      rootEl,
      inputEl,
      open,
      search,
      filtered,
      hoverIndex,
      label,
      openList,
      closeList,
      selectAt,
      onKeydown,
    }
  },
  render() {
    const trigger = h(
      'button',
      {
        type: 'button',
        class:
          'w-full h-10 rounded-xl border-2 border-gray-300 px-3 text-sm bg-white text-gray-900 flex items-center justify-between gap-3 hover:border-gray-400 focus:outline-none',
        onClick: this.open ? this.closeList : this.openList,
        onKeydown: this.onKeydown,
        'aria-haspopup': 'listbox',
        'aria-expanded': this.open ? 'true' : 'false',
      },
      [
        h(
          'span',
          { class: (this.modelValue ? 'text-gray-900' : 'text-gray-400') + ' truncate' },
          this.label
        ),
        h('svg', { viewBox: '0 0 20 20', class: 'w-4 h-4 shrink-0' }, [
          h('path', { d: 'M6 8l4 4 4-4', fill: 'currentColor' }),
        ]),
      ]
    )

    const dropdown = this.open
      ? h(
          'div',
          {
            class:
              'absolute z-50 mt-2 w-full rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden',
          },
          [
            h('div', { class: 'p-2 border-b border-gray-200 bg-gray-50' }, [
              h('input', {
                ref: 'inputEl',
                value: this.search,
                onInput: (e) => (this.search = e.target.value),
                onKeydown: this.onKeydown,
                type: 'text',
                placeholder: 'Search...',
                class:
                  'w-full h-9 rounded-lg border border-gray-300 px-3 text-sm focus:outline-none',
              }),
            ]),
            h(
              'ul',
              { role: 'listbox', class: 'max-h-64 overflow-auto py-1', onKeydown: this.onKeydown },
              this.filtered.length
                ? this.filtered.map((opt, i) =>
                    h(
                      'li',
                      {
                        role: 'option',
                        key: opt.key,
                        class:
                          'px-3 py-2 text-sm cursor-pointer flex items-center justify-between ' +
                          (i === this.hoverIndex ? 'bg-gray-100' : 'bg-white') +
                          (opt.key === this.modelValue ? ' font-semibold' : ' font-normal'),
                        onMouseenter: () => (this.hoverIndex = i),
                        onMousedown: (e) => {
                          e.preventDefault()
                          this.selectAt(i)
                        },
                      },
                      [
                        h('span', { class: 'truncate' }, opt.label),
                        opt.key === this.modelValue
                          ? h('svg', { viewBox: '0 0 20 20', class: 'w-4 h-4' }, [
                              h('path', {
                                d: 'M7.5 13.5l-3-3 1.4-1.4L7.5 10.7l7-7 1.4 1.4-8.4 8.4z',
                                fill: 'currentColor',
                              }),
                            ])
                          : null,
                      ]
                    )
                  )
                : [h('li', { class: 'px-3 py-3 text-sm text-gray-500 select-none' }, 'No results')]
            ),
          ]
        )
      : null

    return h('div', { ref: 'rootEl', class: 'relative' }, [trigger, dropdown])
  },
}

export default {
  name: 'PermissionsEdit',
  components: { ToggleRow, SearchableSelect },

  data() {
    return {
      permissionsReady: false,
      can: { view: false, edit: false },
      loading: false,
      saving: false,
      errorMsg: '',
      header: { name: '', email: '' },
      sections: {},
      selectedKey: '',
      selectedNames: new Set(),
    }
  },

  computed: {
    userId() {
      return this.$route.params.id
    },

    optionsForSelect() {
      return Object.values(this.sections)
        .map((s) => {
          let label = s.label
          if (s.parent === 'Reports') label = `${label} Report`
          return { key: s._key, label }
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    },

    filteredPermissions() {
      const rec = this.sections[this.selectedKey]
      if (!rec) return []
      return (rec.all || []).map((full) => {
        let label
        if (rec.parent === 'Reports') {
          const withoutSuffix = full.replace(/\.view$/, '')
          label = withoutSuffix
            .replaceAll('_', ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase())
        } else {
          label = this.stripPrefix(full)
        }
        return { value: full, label }
      })
    },

    selectedCount() {
      return this.selectedNames.size
    },
  },

  async created() {
    await this.loadPermissions()
    if (this.can.view) await this.fetchData()
  },

  methods: {
    stripPrefix(full) {
      const i = String(full).indexOf('.')
      return i >= 0 ? full.slice(i + 1) : full
    },

    async loadPermissions() {
      if (!permLoaded()) {
        try {
          await initPermissions(true)
        } catch (e) {}
      }

      const p = has('others.permissions')
      this.can.view =
        p || has('others.permissions.view') || has('permissions.view') || has('permissions.access')
      this.can.edit = p || has('others.permissions.edit') || has('permissions.edit')

      this.permissionsReady = true
    },

    async fetchData() {
      this.loading = true
      this.errorMsg = ''
      try {
        const { data } = await api.get(`/accounting/permissions/user/${this.userId}`)

        const u = data?.user || {}
        this.header.name = u?.name || ''
        this.header.email = u?.email || ''

        const src = data?.permissions || {}
        const sections = {}
        const allSelected = new Set()

        const toTitle = (s) => {
          if (!s) return ''
          const str = String(s)
          return str.charAt(0).toUpperCase() + str.slice(1)
        }

        const parents = Object.keys(src).sort((a, b) => a.localeCompare(b))

        for (const parent of parents) {
          let children = src[parent]

          if (Array.isArray(children)) {
            const mapped = {}
            children.forEach((item, idx) => {
              const firstPerm = item?.all?.[0] || ''
              const labelRaw = this.stripPrefix(firstPerm) || `item_${idx + 1}`
              const label = toTitle(labelRaw.replaceAll('_', ' '))
              mapped[label] = item
            })
            children = mapped
          }

          if (!children || typeof children !== 'object') continue

          const childKeys = Object.keys(children).sort((a, b) => a.localeCompare(b))

          for (const child of childKeys) {
            const payload = children[child] || {}
            const allList = Array.isArray(payload.all)
              ? [...payload.all].sort((a, b) => a.localeCompare(b))
              : []

            const grantedArr = Array.isArray(payload.granted) ? payload.granted : []
            const granted = new Set(grantedArr)
            const key = `${parent}::${child}`

            sections[key] = { _key: key, label: child, all: allList, granted, parent }
            granted.forEach((p) => allSelected.add(p))
          }
        }

        this.sections = sections
        this.selectedKey = ''
        this.selectedNames = new Set(allSelected)
      } catch (e) {
        this.header = { name: '', email: '' }
        this.sections = {}
        this.selectedKey = ''
        this.selectedNames = new Set()
        this.errorMsg = e?.response?.data?.message || e?.message || 'Failed to load permissions.'
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: this.errorMsg,
          toast: true,
          position: 'top',
          timer: 2200,
          showConfirmButton: false,
        })
      } finally {
        this.loading = false
      }
    },

    isChecked(name) {
      return this.selectedNames.has(name)
    },

    togglePermission(name, val) {
      if (val) this.selectedNames.add(name)
      else this.selectedNames.delete(name)
      this.selectedNames = new Set(this.selectedNames)
    },

    checkAll(yes) {
      const list = this.filteredPermissions
      if (yes) list.forEach((p) => this.selectedNames.add(p.value))
      else list.forEach((p) => this.selectedNames.delete(p.value))
      this.selectedNames = new Set(this.selectedNames)
    },

    async handleSave() {
      if (!this.can.edit) {
        Swal.fire({
          icon: 'error',
          title: 'Permission denied',
          toast: true,
          position: 'top',
          timer: 2000,
          showConfirmButton: false,
        })
        return
      }
      this.saving = true
      try {
        await api.put(`/accounting/permissions/users/${this.userId}`, {
          permission_names: Array.from(this.selectedNames), // was: permissions
        })
        Swal.fire({
          icon: 'success',
          title: 'Permissions updated',
          toast: true,
          position: 'top',
          timer: 1400,
          showConfirmButton: false,
        })
        await this.$router.replace({ name: 'Permissions' })
      } catch (e) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: e?.response?.data?.message || 'Failed to update permissions.',
          toast: true,
          position: 'top',
          timer: 2200,
          showConfirmButton: false,
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
